import { forwardRef } from "react";
import { cn } from "../../lib/cn.js";

/**
 * Toggle switch — drop-in replacement for a checkbox in the admin forms.
 * Use `checked` + `onCheckedChange` (matches shadcn/Radix conventions) so
 * it composes cleanly with React Hook Form via Controller.
 */
export const Switch = forwardRef(
  ({ checked = false, onCheckedChange, disabled, label, hint, className, id, ...props }, ref) => {
    const inputId = id ?? `sw-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <div className={cn("flex items-start gap-3", className)}>
        <button
          ref={ref}
          type="button"
          role="switch"
          id={inputId}
          aria-checked={checked}
          disabled={disabled}
          onClick={() => !disabled && onCheckedChange?.(!checked)}
          className={cn(
            "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base disabled:cursor-not-allowed disabled:opacity-50",
            checked ? "bg-accent" : "bg-bg-elevated-2"
          )}
          {...props}
        >
          <span
            aria-hidden
            className={cn(
              "inline-block h-4 w-4 transform rounded-full bg-bg-base transition-transform",
              checked ? "translate-x-6" : "translate-x-1"
            )}
          />
        </button>
        {(label || hint) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={inputId}
                className="cursor-pointer text-sm font-medium text-text-primary"
              >
                {label}
              </label>
            )}
            {hint && <p className="text-xs text-text-muted">{hint}</p>}
          </div>
        )}
      </div>
    );
  }
);
Switch.displayName = "Switch";
